import axios from "axios";
import { getApiBaseUrl } from "../config/api";

interface UploadTicket {
  upload_url: string;
  ticket: string;
  resume_token?: string;
}

interface DirectStudyUploadOptions {
  studyUid: string;
  shared: boolean;
  body: Blob;
  contentType: string;
  timeoutMs: number;
  /** Abort and retry when the browser stops sending bytes for this long. */
  stallTimeoutMs?: number;
  /** Total fresh-ticket attempts. Large-study batches retain their outer retry loop. */
  maxAttempts?: number;
  signal?: AbortSignal;
  onProgress?: (loaded: number, total: number) => void;
  batch?: {
    uploadId: string;
    index: number;
    count: number;
    resumeToken?: string;
  };
  onResumeToken?: (token: string) => void;
}

export class DirectUploadError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly code?: "ECONNABORTED" | "ERR_CANCELED",
    readonly detail?: string,
  ) {
    super(message);
    this.name = "DirectUploadError";
  }
}

const DEFAULT_STALL_TIMEOUT_MS = 60_000;
const DEFAULT_MAX_ATTEMPTS = 3;
const UPLOAD_BATCH_ALREADY_PROCESSING = "This upload batch is already being processed";

function makeUploadId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `upload_${Date.now()}_${Math.random().toString(36).slice(2)}`
  );
}

export function isDirectUploadCancelled(error: unknown): boolean {
  return error instanceof DirectUploadError && error.code === "ERR_CANCELED";
}

function parseTicket(value: unknown): UploadTicket {
  if (!value || typeof value !== "object") throw new Error("Upload ticket response is invalid.");
  const {
    upload_url: uploadUrl,
    ticket,
    resume_token: resumeToken,
  } = value as Record<string, unknown>;
  if (typeof uploadUrl !== "string" || typeof ticket !== "string") {
    throw new Error("Upload ticket response is incomplete.");
  }
  if (resumeToken !== undefined && typeof resumeToken !== "string") {
    throw new Error("Upload resume token is invalid.");
  }
  return { upload_url: uploadUrl, ticket, resume_token: resumeToken };
}

async function requestTicket(
  studyUid: string,
  shared: boolean,
  contentLength: number,
  batch?: DirectStudyUploadOptions["batch"],
): Promise<UploadTicket> {
  const response = await axios.post(
    `${getApiBaseUrl()}/api/upload/study/${encodeURIComponent(studyUid)}/ticket${
      shared ? "?shared=true" : ""
    }`,
    batch
      ? {
          content_length: contentLength,
          upload_id: batch.uploadId,
          batch_index: batch.index,
          batch_count: batch.count,
          ...(batch.resumeToken ? { resume_token: batch.resumeToken } : {}),
        }
      : { content_length: contentLength },
  );
  return parseTicket(response.data);
}

function responseDetail(value: unknown): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  const detail = (value as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;
  const nested =
    detail && typeof detail === "object" ? (detail as { message?: unknown }).message : null;
  return typeof nested === "string" ? nested : undefined;
}

function postStudy(ticket: UploadTicket, options: DirectStudyUploadOptions): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let abortReason: "user" | "stall" | null = null;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const stallTimeoutMs = options.stallTimeoutMs ?? DEFAULT_STALL_TIMEOUT_MS;
    const abort = () => {
      abortReason = "user";
      xhr.abort();
    };
    const clearStallTimer = () => {
      if (stallTimer !== undefined) clearTimeout(stallTimer);
      stallTimer = undefined;
    };
    const armStallTimer = () => {
      clearStallTimer();
      stallTimer = setTimeout(() => {
        abortReason = "stall";
        xhr.abort();
      }, stallTimeoutMs);
    };
    const finish = () => {
      clearStallTimer();
      options.signal?.removeEventListener("abort", abort);
    };

    xhr.open("POST", ticket.upload_url);
    xhr.responseType = "json";
    xhr.timeout = options.timeoutMs;
    xhr.setRequestHeader("Authorization", `Bearer ${ticket.ticket}`);
    xhr.setRequestHeader("Content-Type", options.contentType);
    xhr.upload.onprogress = (event) => {
      if (event.loaded < event.total || !event.lengthComputable) armStallTimer();
      else clearStallTimer();
      if (event.lengthComputable) options.onProgress?.(event.loaded, event.total);
    };
    xhr.onload = () => {
      finish();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response);
        return;
      }
      reject(
        new DirectUploadError(
          `Upload rejected (${xhr.status})`,
          xhr.status,
          undefined,
          responseDetail(xhr.response),
        ),
      );
    };
    xhr.onerror = () => {
      finish();
      reject(new DirectUploadError("Upload network error"));
    };
    xhr.ontimeout = () => {
      finish();
      reject(new DirectUploadError("Upload timed out", undefined, "ECONNABORTED"));
    };
    xhr.onabort = () => {
      finish();
      if (abortReason === "stall") {
        reject(new DirectUploadError("Upload stalled", undefined, "ECONNABORTED"));
        return;
      }
      reject(new DirectUploadError("Upload cancelled", undefined, "ERR_CANCELED"));
    };
    if (options.signal?.aborted) {
      reject(new DirectUploadError("Upload cancelled", undefined, "ERR_CANCELED"));
      return;
    }
    options.signal?.addEventListener("abort", abort, { once: true });
    armStallTimer();
    xhr.send(options.body);
  });
}

function isRetryable(error: unknown): boolean {
  if (isDirectUploadCancelled(error)) return false;
  if (error instanceof DirectUploadError) {
    if (error.code === "ECONNABORTED" || error.status == null) return true;
    if (error.status === 409) return error.detail === UPLOAD_BATCH_ALREADY_PROCESSING;
    return (
      error.status === 401 || error.status === 408 || error.status === 429 || error.status >= 500
    );
  }
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    return status == null || status === 408 || status === 429 || status >= 500;
  }
  return false;
}

async function waitForRetry(ms: number, signal?: AbortSignal): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
  if (signal?.aborted) {
    throw new DirectUploadError("Upload cancelled", undefined, "ERR_CANCELED");
  }
}

export async function uploadStudyDirect(options: DirectStudyUploadOptions): Promise<unknown> {
  const maxAttempts = Math.max(1, options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS);
  // Keep one receiver identity across every fresh-ticket retry. This lets the
  // receiver return the completed receipt without publishing inference again
  // when the original response was lost after the body was persisted.
  let batch = options.batch ?? {
    uploadId: makeUploadId(),
    index: 0,
    count: 1,
  };
  let lastError: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const ticket = await requestTicket(
        options.studyUid,
        options.shared,
        options.body.size,
        batch,
      );
      if (ticket.resume_token) {
        options.onResumeToken?.(ticket.resume_token);
        if (batch) batch = { ...batch, resumeToken: ticket.resume_token };
      }
      return await postStudy(ticket, options);
    } catch (error) {
      lastError = error;
      if (attempt === maxAttempts - 1 || !isRetryable(error)) throw error;
      await waitForRetry(500 * 2 ** attempt, options.signal);
    }
  }
  throw lastError;
}
